export type Product = {
  label: string;
  seoTitle: string;
  metaDescription: string;
  hero: {
    title: string;
    tagline: string;
    body: string[];
    primaryCta: string;
  };
  problem: {
    heading: string;
    body: string[];
    bullets?: string[];
    closing?: string;
  };
  what: {
    heading: string;
    subheading: string;
    body: string[];
    bullets?: string[];
    closing?: string;
  };
  how: {
    heading: string;
    steps: { title: string; body: string }[];
  };
  features: string[];
  useCases: {
    intro: string;
    bullets: string[];
  };
  why: {
    heading: string;
    body: string[];
  };
  closing: {
    heading: string;
    body: string;
    primaryCta: string;
  };
};

export const products: Record<string, Product> = {
  "market-intelligence": {
    label: "Market intelligence",
    seoTitle: "AI Market Intelligence Platform | Axy.digital",
    metaDescription:
      "Track market signals, search demand, competitor movement, and social narratives in real time. Axy.digital turns market intelligence into campaigns automatically.",
    hero: {
      title: "Market Intelligence",
      tagline:
        "AI market intelligence for teams that need to know what customers care about before competitors turn it into campaigns.",
      body: [
        "Markets move faster than most marketing teams can track manually. New competitors launch. Narratives shift. Customers start searching for new problems. Trends spike across LinkedIn, X, Reddit, Google, and AI search before they show up in your dashboards.",
        "Axy.digital tracks those signals in real time and turns them into campaign opportunities.",
        "Know what your market is talking about. Understand where demand is moving. Act before the moment passes.",
      ],
      primaryCta: "Track your market signals",
    },
    problem: {
      heading: "Your market is changing faster than your planning cycle",
      body: [
        "Most marketing teams still plan around static calendars, quarterly strategies, and manual research.",
        "But demand does not move quarterly. It moves when:",
      ],
      bullets: [
        "A competitor launches a new product",
        "A regulation changes",
        "A customer pain point starts trending",
        "A new category narrative appears",
        "A founder post goes viral",
        "A market report shifts buyer priorities",
        "Search demand spikes around a specific problem",
        "AI tools start recommending competitors instead of you",
      ],
      closing:
        "By the time most teams notice the shift, the best window to capture attention has already passed.",
    },
    what: {
      heading: "Turn live market signals into campaign direction",
      subheading: "What Market Intelligence does",
      body: [
        "Axy.digital's Market Intelligence tracks the signals shaping demand across your industry, competitors, customers, and channels. It helps you understand:",
      ],
      bullets: [
        "What your audience is searching for",
        "What narratives are gaining momentum",
        "What competitors are publishing",
        "What topics are trending across LinkedIn, X, Reddit, and the web",
        "What questions customers are asking in AI search",
        "What campaigns you should generate next",
      ],
      closing:
        "Instead of starting from a blank page, Axy.digital gives you a live view of where your market is moving and what to do about it.",
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Define your market",
          body: "Axy.digital learns your company, category, ICP, competitors, products, positioning, and target regions.",
        },
        {
          title: "Track live signals",
          body: "The engine monitors search trends, competitor content, industry news, AI prompts, social conversations, and emerging narratives.",
        },
        {
          title: "Identify campaign opportunities",
          body: "Axy.digital surfaces the topics, questions, and moments most relevant to your business.",
        },
        {
          title: "Generate recommended campaigns",
          body: "Turn each signal into SEO articles, GEO content, LinkedIn posts, X posts, or full cross-channel campaigns.",
        },
        {
          title: "Learn from performance",
          body: "Axy.digital feeds results back into future recommendations so your campaigns get more relevant over time.",
        },
      ],
    },
    features: [
      "Real-time market signal tracking",
      "Competitor content monitoring",
      "Search demand insights",
      "Social trend detection",
      "Narrative shift analysis",
      "Industry news tracking",
      "AI search prompt opportunities",
      "Campaign recommendations",
      "Segment and region-specific insights",
      "Signal-to-content workflows",
    ],
    useCases: {
      intro: "Use Market Intelligence to:",
      bullets: [
        "Find emerging topics before competitors own them",
        "Track what buyers are actively searching for",
        "Monitor competitor positioning and content",
        "Identify content gaps in your category",
        "Generate campaigns from real-time demand",
        "Brief founders, marketers, and sales teams with fresh insights",
        "Spot narrative shifts across regions or verticals",
        "Build a faster, more responsive marketing engine",
      ],
    },
    why: {
      heading: "The best marketing starts before the market peaks",
      body: [
        "Most teams create content after a topic is obvious. Axy.digital helps you act earlier.",
        "When you can see demand forming, you can publish while the conversation is still active. That means your content has a better chance of being discovered, cited, shared, and remembered.",
        "This is the foundation of algorithmic marketing: using live signals to decide what to say, where to say it, and when to act.",
      ],
    },
    closing: {
      heading: "Stop guessing what your market wants",
      body: "Axy.digital tracks the signals shaping demand and turns them into campaigns automatically.",
      primaryCta: "Track your market signals",
    },
  },

  "ai-visibility-analytics": {
    label: "AI visibility analytics",
    seoTitle: "AI Visibility Analytics & GEO Reporting | Axy.digital",
    metaDescription:
      "See where your brand appears across ChatGPT, Perplexity, Gemini, Claude, and AI search. Track mentions, citations, competitors, and GEO opportunities.",
    hero: {
      title: "AI Visibility Analytics",
      tagline:
        "AI visibility analytics for teams that need to know whether their brand is being found, cited, and recommended by AI search engines.",
      body: [
        "Your customers are no longer only searching on Google. They are asking ChatGPT, Perplexity, Gemini, Claude, and other AI systems who to trust, what to buy, and which companies to compare.",
        "Axy.digital shows where your brand appears in those answers, which competitors are winning visibility, and what content you need to create next.",
        "Measure your AI search visibility. Find the gaps. Improve your citation share.",
      ],
      primaryCta: "Get your AI visibility report",
    },
    problem: {
      heading: "If AI does not mention you, buyers may never find you",
      body: ["Search behavior is changing. Customers now ask AI tools questions like:"],
      bullets: [
        "“Best software for…”",
        "“Top law firms for…”",
        "“Which cybersecurity companies solve…”",
        "“Best logistics provider for…”",
        "“Alternatives to…”",
        "“Who are the leading companies in…”",
      ],
      closing:
        "These answers shape buyer perception before someone visits your website. But most teams have no idea whether they appear in AI-generated answers, which prompts mention their brand, which competitors are being recommended instead, which pages are being cited, which topics they need to own, or whether their GEO strategy is improving over time. Traditional SEO dashboards do not answer these questions.",
    },
    what: {
      heading: "See where your brand stands in AI search",
      subheading: "What AI Visibility Analytics does",
      body: [
        "Axy.digital's AI Visibility Analytics tracks how your brand appears across AI search and answer engines. It shows:",
      ],
      bullets: [
        "Which prompts mention your brand",
        "Which prompts cite your website",
        "Which competitors appear more often",
        "Which LLMs recommend you",
        "Which topics you are missing from",
        "Which pages are being used as sources",
        "What content to create to improve visibility",
      ],
      closing:
        "This gives you a measurable way to improve AI discovery, GEO, AEO, and citation share.",
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Map your category prompts",
          body: "Axy.digital identifies high-intent prompts your buyers are likely asking across your category, use cases, competitors, and pain points.",
        },
        {
          title: "Test your AI visibility",
          body: "The engine checks whether your brand is mentioned, cited, or ignored across AI search engines.",
        },
        {
          title: "Benchmark competitors",
          body: "Axy.digital compares your visibility against competitors to show who is winning the answers your buyers see.",
        },
        {
          title: "Identify content gaps",
          body: "The system finds the prompts and topics where you need stronger pages, articles, FAQs, or authority signals.",
        },
        {
          title: "Generate GEO recommendations",
          body: "Turn visibility gaps into articles, landing pages, LinkedIn posts, and cross-channel campaigns designed to improve discoverability.",
        },
      ],
    },
    features: [
      "Prompt-level visibility tracking",
      "Mention rate analytics",
      "Citation rate analytics",
      "Competitor visibility benchmarking",
      "LLM-by-LLM visibility breakdown",
      "GEO performance reporting",
      "Prompt cluster mapping",
      "Source and citation analysis",
      "Content gap recommendations",
      "Recommended campaigns",
      "Visibility trend tracking over time",
    ],
    useCases: {
      intro: "Use AI Visibility Analytics to:",
      bullets: [
        "See whether AI tools recommend your brand",
        "Track your share of AI-generated answers",
        "Compare visibility against competitors",
        "Find prompts where your brand is missing",
        "Improve citation share across AI search",
        "Prioritize GEO and AEO content",
        "Report on visibility beyond Google rankings",
        "Understand which content AI systems are citing",
      ],
    },
    why: {
      heading: "AI answers are becoming a new distribution channel",
      body: [
        "In traditional SEO, visibility meant ranking on a search results page.",
        "In AI search, visibility means being mentioned, cited, summarized, and recommended inside the answer itself. That changes how brands need to compete.",
        "Axy.digital helps you measure and improve the new version of organic visibility: not just where you rank, but whether AI systems trust your brand enough to include you.",
      ],
    },
    closing: {
      heading: "Find out if AI is recommending your competitors instead of you",
      body: "Axy.digital shows where your brand appears, where it is missing, and what to do next.",
      primaryCta: "Get your AI visibility report",
    },
  },

  "cross-channel-social-analytics": {
    label: "Cross-channel social analytics",
    seoTitle: "LinkedIn & X Analytics for AI Marketing | Axy.digital",
    metaDescription:
      "Track LinkedIn and X performance across posts, topics, founders, and channels. Axy.digital turns social analytics into better campaigns automatically.",
    hero: {
      title: "Cross-Channel Social Analytics",
      tagline:
        "Social analytics for teams that need to understand which narratives, posts, and channels are actually building reach, engagement, and demand.",
      body: [
        "Most social analytics show what happened. Axy.digital helps you understand what to do next.",
        "Track performance across LinkedIn and X, compare what works across channels, and feed those insights back into future campaigns automatically.",
        "Know what resonates. Double down faster. Stop posting into the void.",
      ],
      primaryCta: "Analyze your social performance",
    },
    problem: {
      heading: "Social performance is scattered across too many disconnected views",
      body: [
        "Most teams are active on LinkedIn and X, but they struggle to understand what is actually working.",
        "They can see likes, impressions, comments, and reposts, but not always:",
      ],
      bullets: [
        "Which narratives are compounding",
        "Which topics attract the right audience",
        "Which founder posts drive the most engagement",
        "Which channels perform better for which messages",
        "Which market signals should become more content",
        "Which posts are worth turning into blogs, threads, or campaigns",
        "Whether social activity is building authority over time",
      ],
      closing:
        "The result is inconsistent posting, weak feedback loops, and too much manual interpretation.",
    },
    what: {
      heading: "Turn social performance into a learning loop",
      subheading: "What Cross-Channel Social Analytics does",
      body: [
        "Axy.digital's Cross-Channel Social Analytics helps you understand how content performs across LinkedIn and X.",
        "It tracks post performance, topic performance, narrative traction, account-level growth, and channel differences.",
        "Then it feeds those insights into future recommendations, so your next campaigns are based on what your audience actually responds to.",
      ],
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Connect your channels",
          body: "Axy.digital tracks performance across your LinkedIn and X accounts.",
        },
        {
          title: "Measure post and topic performance",
          body: "See which posts, topics, formats, and narratives drive reach and engagement.",
        },
        {
          title: "Compare across channels",
          body: "Understand what works better on LinkedIn, what works better on X, and where each channel fits into your GTM motion.",
        },
        {
          title: "Identify repeatable patterns",
          body: "Axy.digital finds the messages, formats, and angles that are worth repeating or expanding.",
        },
        {
          title: "Feed insights into future campaigns",
          body: "Use performance data to generate better LinkedIn posts, X posts, threads, and cross-channel campaigns.",
        },
      ],
    },
    features: [
      "LinkedIn performance tracking",
      "X performance tracking",
      "Cross-channel comparison",
      "Founder account analytics",
      "Company account analytics",
      "Post-level performance",
      "Topic and narrative performance",
      "Engagement and reach trends",
      "Content format analysis",
      "Campaign feedback loops",
      "Recommendations for future posts",
    ],
    useCases: {
      intro: "Use Cross-Channel Social Analytics to:",
      bullets: [
        "See which topics are resonating",
        "Compare LinkedIn and X performance",
        "Track founder-led thought leadership",
        "Improve future content recommendations",
        "Identify posts worth expanding into blogs or campaigns",
        "Understand audience response by channel",
        "Report on social growth and engagement",
        "Build a stronger feedback loop between content and performance",
      ],
    },
    why: {
      heading: "Social content should get smarter every week",
      body: [
        "Most teams treat social as an output channel. Axy.digital treats social as a learning system.",
        "Every post creates data about what your market cares about, what your audience responds to, and which narratives are worth expanding.",
        "When that data feeds back into your marketing engine, your content becomes more relevant over time.",
      ],
    },
    closing: {
      heading: "Build a social engine that learns from every post",
      body: "Axy.digital turns LinkedIn and X performance into better recommendations, stronger campaigns, and more relevant content.",
      primaryCta: "Analyze your social performance",
    },
  },

  "geo-aeo-seo-autopilot": {
    label: "GEO / AEO / SEO autopilot",
    seoTitle: "GEO, AEO & SEO Autopilot | Axy.digital",
    metaDescription:
      "Automatically generate GEO, AEO, and SEO content from real search demand. Axy.digital helps your brand get found, cited, and recommended across Google and AI search.",
    hero: {
      title: "GEO, AEO & SEO Autopilot\n",
      tagline:
        "Win AI search without a team. Search and AI discovery content generated automatically from real market demand.",
      body: [
        "Customers are searching differently. They still use Google, but they also ask ChatGPT, Perplexity, Gemini, and other AI tools for answers, recommendations, and comparisons.",
        "Axy.digital helps you generate content built for both traditional search and AI discovery, using live demand signals, prompt clusters, and visibility gaps.",
        "Create content designed to be found, cited, and recommended.",
      ],
      primaryCta: "Generate GEO content",
    },
    problem: {
      heading: "SEO alone is no longer enough",
      body: [
        "Organic discovery used to be mostly about ranking on Google. Now your buyers are also asking AI systems:",
      ],
      bullets: [
        "What company should I use?",
        "What are the best tools for this problem?",
        "What are the alternatives to this product?",
        "Who are the leaders in this category?",
        "What should I know before buying?",
        "Which provider is best for my use case?",
      ],
      closing:
        "If your content is not structured for AI retrieval, citation, and recommendation, your brand may be absent from the answers shaping buyer decisions. At the same time, producing content manually is slow, expensive, and often disconnected from real demand.",
    },
    what: {
      heading: "Generate content for the new search journey",
      subheading: "What GEO, AEO & SEO Autopilot does",
      body: [
        "Axy.digital's GEO, AEO & SEO Autopilot turns search demand, AI prompts, competitor gaps, and market signals into content automatically. It helps you create:",
      ],
      bullets: [
        "SEO articles",
        "GEO-optimized articles",
        "AEO-style answer content",
        "Prompt-cluster content",
        "Comparison pages",
        "Use-case pages",
        "FAQ sections",
        "Internal linking recommendations",
        "Content refresh recommendations",
      ],
      closing:
        "The goal is not more content for its own sake. The goal is to help your brand become easier to find, cite, and recommend.",
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Map demand",
          body: "Axy.digital identifies the keywords, prompts, questions, and topics your customers are already searching.",
        },
        {
          title: "Find visibility gaps",
          body: "The engine shows where your brand is missing across Google and AI search.",
        },
        {
          title: "Generate content briefs",
          body: "Axy.digital creates structured briefs designed around search intent, AI retrieval, and citation potential.",
        },
        {
          title: "Produce optimized content",
          body: "Generate articles, landing pages, FAQs, and supporting assets aligned with your brand context.",
        },
        {
          title: "Feed results back into the engine",
          body: "Use visibility, citation, and performance data to improve future content recommendations.",
        },
      ],
    },
    features: [
      "SEO article generation",
      "GEO article generation",
      "AEO article generation",
      "Prompt cluster mapping",
      "Search demand analysis",
      "[AI visibility](/geo/what-is-ai-visibility) gap detection",
      "Competitor content gap analysis",
      "Content brief generation",
      "Internal linking recommendations",
      "FAQ and structured content recommendations",
      "Content refresh workflows",
      "Cross-channel campaign generation",
    ],
    useCases: {
      intro: "Use GEO, AEO & SEO Autopilot to:",
      bullets: [
        "Capture high-intent search demand",
        "Improve AI search visibility",
        "Build citation-worthy content",
        "Create articles around prompt clusters",
        "Generate comparison and alternative pages",
        "Answer customer questions at scale",
        "Support long-tail organic discovery",
        "Turn market signals into search content",
        "Refresh outdated content based on new demand",
      ],
    },
    why: {
      heading: "The next version of organic growth is citation-driven",
      body: [
        "In the AI discovery era, buyers may not click through ten blue links. They may ask one question and trust the answer.",
        "That means your content needs to be structured, specific, useful, and authoritative enough to be retrieved, summarized, and cited.",
        "Axy.digital helps you build that content system continuously.",
      ],
    },
    closing: {
      heading: "Generate content for how buyers search now",
      body: "Axy.digital turns live demand into SEO, AEO, and GEO content built to improve discovery across Google and AI search.",
      primaryCta: "Generate search content",
    },
  },

  "linkedin-autopilot": {
    label: "LinkedIn autopilot",
    seoTitle: "LinkedIn Autopilot for Founder-Led Marketing | Axy.digital",
    metaDescription:
      "Turn market signals into LinkedIn thought leadership automatically. Axy.digital helps founders and teams create timely posts that build reach, authority, and demand.",
    hero: {
      title: "LinkedIn Autopilot",
      tagline:
        "LinkedIn autopilot for founders and teams that need to turn market signals into thought leadership without starting from a blank page.",
      body: [
        "Your buyers, investors, partners, and competitors are already on LinkedIn. The challenge is not whether you should post. It is knowing what to say, when to say it, and how to stay consistent without spending hours managing content.",
        "Axy.digital turns live market signals into LinkedIn posts that build authority, reach, and demand.",
        "Stay visible. Build trust. Publish while the conversation is still active.",
      ],
      primaryCta: "Generate LinkedIn posts",
    },
    problem: {
      heading: "Founder-led LinkedIn works, but it is hard to sustain",
      body: ["Most teams know LinkedIn matters. But consistent posting breaks down because:"],
      bullets: [
        "Founders are busy",
        "Ideas stay trapped in calls and notes",
        "Content calendars go stale",
        "Posts become generic",
        "Market moments are missed",
        "Agencies do not capture founder voice",
        "AI tools require too much prompting",
        "There is no clear feedback loop from performance",
      ],
      closing:
        "The result is sporadic posting and missed opportunities to shape the market conversation.",
    },
    what: {
      heading: "Turn market context into thought leadership",
      subheading: "What LinkedIn Autopilot does",
      body: [
        "Axy.digital's LinkedIn Autopilot generates LinkedIn content from live market signals, company context, founder perspective, and performance data. It helps you create:",
      ],
      bullets: [
        "Founder thought leadership",
        "Company page posts",
        "Market commentary",
        "Product education posts",
        "Case study posts",
        "Narrative-led posts",
        "Launch posts",
        "Trend response posts",
        "Sales-supporting content",
      ],
      closing:
        "You can review, edit, approve, and publish without building each post from scratch.",
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Learn your voice and context",
          body: "Axy.digital learns your company, positioning, audience, founder perspective, and content preferences.",
        },
        {
          title: "Track relevant market signals",
          body: "The engine monitors industry news, competitor movement, customer questions, and social narratives.",
        },
        {
          title: "Generate LinkedIn-ready posts",
          body: "Axy.digital turns signals into posts aligned with your voice, strategy, and goals.",
        },
        {
          title: "Review and approve",
          body: "You stay in control of what goes live. Edit, refine, or approve posts before publishing.",
        },
        {
          title: "Learn from performance",
          body: "Post performance feeds back into future recommendations, helping Axy.digital understand what resonates.",
        },
      ],
    },
    features: [
      "Founder thought leadership generation",
      "Company page post generation",
      "Market signal-based post ideas",
      "LinkedIn content calendar",
      "Voice and positioning alignment",
      "Approval workflows",
      "Post editing",
      "Scheduling and publishing",
      "Performance feedback",
      "Topic recommendations",
      "Campaign-to-post generation",
    ],
    useCases: {
      intro: "Use LinkedIn Autopilot to:",
      bullets: [
        "Build founder-led thought leadership",
        "Stay consistent on LinkedIn",
        "Respond to market trends quickly",
        "Turn product updates into posts",
        "Turn blogs into LinkedIn content",
        "Share case studies and proof points",
        "Support sales and fundraising",
        "Build authority in your category",
        "Keep your company page active",
      ],
    },
    why: {
      heading: "LinkedIn rewards relevance, timing, and consistency",
      body: [
        "Great LinkedIn content is not just about posting often. It is about showing up with the right perspective while your market is already paying attention.",
        "Axy.digital helps you move from forced posting to signal-led thought leadership, so every post has a reason to exist.",
      ],
    },
    closing: {
      heading: "Turn your market perspective into consistent LinkedIn visibility",
      body: "Axy.digital helps founders and teams generate LinkedIn content from real market demand, without starting from scratch every week.",
      primaryCta: "Generate LinkedIn posts",
    },
  },

  "x-autopilot": {
    label: "X autopilot",
    seoTitle: "X/Twitter Autopilot for Founder-Led Marketing | Axy.digital",
    metaDescription:
      "Generate X posts and threads from market signals, industry narratives, and founder voice. Axy.digital helps teams stay visible on X without manual content planning.",
    hero: {
      title: "X Autopilot",
      tagline:
        "X autopilot for teams that need to turn fast-moving market narratives into timely posts, threads, and commentary.",
      body: [
        "X moves quickly. Narratives form, spike, mutate, and disappear faster than most teams can plan content around them.",
        "Axy.digital helps you track what your market is talking about and turn those signals into X content that keeps your brand visible.",
        "Move at the speed of the conversation without living on the timeline.",
      ],
      primaryCta: "Generate X posts",
    },
    problem: {
      heading: "X is high-signal, but hard to manage manually",
      body: [
        "For many categories, X is where narratives start. It is where founders, investors, developers, operators, researchers, analysts, and communities react in real time.",
        "But using X effectively is difficult because:",
      ],
      bullets: [
        "The timeline moves too quickly",
        "Trends are hard to separate from noise",
        "Good posts require timing and point of view",
        "Threads take time to structure",
        "Founder voice is hard to outsource",
        "Generic AI posts perform poorly",
        "Consistency is difficult to maintain",
      ],
      closing: "Most teams either ignore X or post reactively without a clear system.",
    },
    what: {
      heading: "Turn live narratives into X content",
      subheading: "What X Autopilot does",
      body: [
        "Axy.digital's X Autopilot tracks relevant conversations, market signals, competitor movement, and industry narratives. Then it generates X-ready content aligned with your company, voice, and strategy. It helps you create:",
      ],
      bullets: [
        "Short posts",
        "Threads",
        "Market commentary",
        "Founder posts",
        "Product updates",
        "Launch content",
        "Trend responses",
        "Community education posts",
        "Campaign extensions from blogs and LinkedIn",
      ],
    },
    how: {
      heading: "How it works",
      steps: [
        {
          title: "Learn your market and voice",
          body: "Axy.digital learns your company, positioning, audience, competitors, and preferred tone.",
        },
        {
          title: "Track relevant conversations",
          body: "The engine monitors market narratives, social signals, competitor posts, and industry discussions.",
        },
        {
          title: "Generate X posts and threads",
          body: "Axy.digital turns signals into short-form posts, commentary, threads, and campaign assets.",
        },
        {
          title: "Review and schedule",
          body: "Approve, edit, and schedule content before it goes live.",
        },
        {
          title: "Learn from engagement",
          body: "Performance data feeds back into future recommendations, helping the engine improve over time.",
        },
      ],
    },
    features: [
      "X post generation",
      "Thread generation",
      "Market narrative tracking",
      "Trend response content",
      "Founder voice alignment",
      "Campaign-to-X repurposing",
      "Product update posts",
      "Community education posts",
      "Scheduling and publishing",
      "Engagement analytics",
      "Performance-based recommendations",
    ],
    useCases: {
      intro: "Use X Autopilot to:",
      bullets: [
        "Stay visible in fast-moving conversations",
        "Turn market signals into timely posts",
        "Build founder and brand presence",
        "Generate threads from blogs, reports, and product updates",
        "Respond to ecosystem narratives",
        "Support launches and announcements",
        "Engage technical, investor, or community audiences",
        "Repurpose campaigns into short-form content",
      ],
    },
    why: {
      heading: "X is where timing matters most",
      body: [
        "On X, a good idea posted too late often disappears.",
        "Axy.digital helps you detect what is gaining traction and respond while the conversation is still active.",
        "Instead of forcing your team to live online, Axy.digital gives you a system for turning market awareness into timely visibility.",
      ],
    },
    closing: {
      heading: "Stay visible without living on X",
      body: "Axy.digital turns fast-moving market conversations into timely posts, threads, and campaigns.",
      primaryCta: "Generate X posts",
    },
  },
};
